import { useCallback, useEffect, useState } from 'react';
import { CaretDown, CaretUp, Plus } from '@phosphor-icons/react';

import { groupKey, splitGroupCategories } from './budgetGroupCategories';
import { BudgetGroupIcon } from './BudgetGroupIcon';
import { BudgetGroupRefBar } from './BudgetGroupRefBar';
import {
  BudgetCategoryFocusPanel,
  resolveCategoryId,
} from './BudgetCategoryFocusPanel';
import { CategoryBudgetSliderRow } from './CategoryBudgetSliderRow';
import { resolveBudgetGroupSubtitle } from './budgetGroupUtils';
import { CardErrorState } from '../common/CardErrorState';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/** Fatia do limite do grupo que cabe a cada categoria (slot `p` do `.pen`). */
function categorySharePct(category, defined) {
  if (!Number.isFinite(defined) || defined <= 0) return null;
  const budget = Number(category.budget_eur ?? 0);
  if (!Number.isFinite(budget) || budget <= 0) return 0;
  return Math.min(100, (budget / defined) * 100);
}

/**
 * Lista de categorias do grupo — `.pen` `ds/card/budget-group` › `body`
 * (padding `[0, $space-5, $space-4, $space-5]`, linhas `ds/row/budget-group-item`).
 */
function GroupCategoryRows({
  categories,
  defined,
  canEditBudgets,
  onAdjustDialog,
  onFocus,
}) {
  return categories.map((cat) => (
    <CategoryBudgetSliderRow
      key={cat.pot_id || cat.category_id}
      category={cat}
      sharePct={categorySharePct(cat, defined)}
      canEditBudgets={canEditBudgets}
      onAdjustDialog={onAdjustDialog}
      onFocus={onFocus}
    />
  ));
}

/** Pé do cartão — `.pen` família group-card › acção «adicionar». */
function GroupAddCategoryFooter({ groupLabel, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="type-body-sm flex w-full items-center justify-center gap-space-2 pt-space-3 font-semibold text-sage-on-tint transition-colors hover:text-sage-700"
    >
      <Plus className="h-4 w-4" aria-hidden />
      {pt.budget.categories.addToGroup.replace('{group}', groupLabel)}
    </button>
  );
}

/**
 * Variante de grelha (desktop) — mesmo cartão `ds/card/budget-group`, sempre
 * aberto (sem cauda de expandir).
 */
function BudgetGroupSectionDesktop({
  group,
  hasIncome,
  canEditBudgets,
  canAddCategory,
  onAdjustDialog,
  onCreateCategory,
  onFocusChange,
}) {
  const categories = splitGroupCategories(group);
  const groupLabel = group.name || pt.categories.form.noGroup;
  const subtitle = resolveBudgetGroupSubtitle(group);
  const defined = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);
  const gKey = groupKey(group);

  return (
    <section className="flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border border-border bg-card">
      <div className="flex w-full min-w-0 items-center gap-space-3 px-space-5 py-space-4">
        <BudgetGroupIcon icon={group.icon} groupName={group.name} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="type-title-card truncate font-display text-card-foreground">
            {groupLabel}
          </p>
          {subtitle ? (
            <p className="type-label-meta truncate text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        <span className="type-body-sm tnum shrink-0 font-display font-semibold text-card-foreground">
          {formatEUR(defined)}
        </span>
      </div>

      <BudgetGroupRefBar group={group} hasIncome={hasIncome} />

      {categories.length > 0 || canAddCategory ? (
        <div className="flex min-w-0 flex-col border-t border-border px-space-5 pb-space-4">
          <GroupCategoryRows
            categories={categories}
            defined={defined}
            canEditBudgets={canEditBudgets}
            onAdjustDialog={onAdjustDialog}
            onFocus={(category) => {
              const catId = resolveCategoryId(category);
              if (catId) onFocusChange?.({ groupKey: gKey, categoryId: catId });
            }}
          />
          {canAddCategory ? (
            <GroupAddCategoryFooter
              groupLabel={groupLabel}
              onClick={() => onCreateCategory(group)}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Cartão de grupo do orçamento — `.pen` `ds/card/budget-group` (`ePTJ3`) na
 * gramática da família group-card:
 *   marcador → cabeçalho com total → faixa de referência → itens → pé.
 *
 * Tokens do `.pen`:
 *   card    `$surface-card` · `$radius-ds-lg` · stroke `$border` (aberto: `$sage-border`)
 *   top     padding `[$space-4, $space-5]` · gap `$space-3` · center
 *     title `$font-display` `$type-title-card` 600 · `$fg-card`
 *     meta  `$type-label` normal · `$fg-muted`
 *     total `$font-display` `$type-body-sm` 600 · `$fg-card`
 *   body    stroke top `$border` · padding `[0, $space-5, $space-4, $space-5]`
 */
function BudgetGroupSection({
  group,
  hasIncome,
  canEditBudgets,
  canAddCategory,
  expanded,
  onToggle,
  onAdjustDialog,
  onCreateCategory,
  firstCycle,
  focusPlacement,
  focusedCategoryId,
  onFocusChange,
}) {
  const categories = splitGroupCategories(group);
  const groupLabel = group.name || pt.categories.form.noGroup;
  const subtitle = resolveBudgetGroupSubtitle(group);
  const gKey = groupKey(group);
  const defined = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);

  const inlineFocus =
    focusPlacement === 'inline'
    && focusedCategoryId
    && categories.some((cat) => resolveCategoryId(cat) === focusedCategoryId);

  const focusedCategory = inlineFocus
    ? categories.find((cat) => resolveCategoryId(cat) === focusedCategoryId)
    : null;
  const siblings = focusedCategory
    ? categories.filter((cat) => resolveCategoryId(cat) !== focusedCategoryId)
    : [];

  const handleToggle = () => {
    if (inlineFocus) onFocusChange?.(null);
    onToggle();
  };

  const handleFocus = (category) => {
    const catId = resolveCategoryId(category);
    if (!catId) return;
    onFocusChange?.({ groupKey: gKey, categoryId: catId });
  };

  if (focusedCategory) {
    return (
      <BudgetCategoryFocusPanel
        group={group}
        category={focusedCategory}
        siblings={siblings}
        canEditBudgets={canEditBudgets}
        onAdjustDialog={onAdjustDialog}
        onBack={() => onFocusChange?.(null)}
        onSelectSibling={handleFocus}
      />
    );
  }

  const isSideFocused =
    focusPlacement === 'side'
    && focusedCategoryId
    && categories.some((cat) => resolveCategoryId(cat) === focusedCategoryId);

  const Chevron = expanded ? CaretUp : CaretDown;

  return (
    <section
      className={cn(
        'flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border bg-card',
        expanded ? 'border-sage-border' : 'border-border',
        isSideFocused && 'ring-2 ring-sage-500/50',
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className="flex w-full min-w-0 items-center gap-space-3 px-space-5 py-space-4 text-left transition-colors hover:bg-muted/50"
      >
        <BudgetGroupIcon icon={group.icon} groupName={group.name} />
        <div className="min-w-0 flex-1">
          <p className="type-title-card truncate font-display text-card-foreground">
            {groupLabel}
          </p>
          {subtitle && !firstCycle ? (
            <p className="type-label-meta truncate text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        <span className="type-body-sm tnum shrink-0 font-display font-semibold text-card-foreground">
          {formatEUR(defined)}
        </span>
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      <BudgetGroupRefBar group={group} hasIncome={hasIncome} />

      {expanded ? (
        <div className="flex min-w-0 flex-col border-t border-border px-space-5 pb-space-4">
          {categories.length > 0 ? (
            <GroupCategoryRows
              categories={categories}
              defined={defined}
              canEditBudgets={canEditBudgets}
              onAdjustDialog={onAdjustDialog}
              onFocus={handleFocus}
            />
          ) : (
            <p className="type-body-sm py-space-3 text-muted-foreground">
              {pt.expenses.byGroup.noCategories}
            </p>
          )}

          {canAddCategory ? (
            <GroupAddCategoryFooter
              groupLabel={groupLabel}
              onClick={() => onCreateCategory(group)}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Grupos do orçamento — Camada 2 (barra + expand) e Camada 3 (foco inline ou via parent).
 */
export function BudgetByGroupList({
  groups,
  hasIncome,
  canEditBudgets,
  canAddCategory,
  onAdjustDialog,
  onCreateCategory,
  firstCycle = false,
  focusPlacement = 'inline',
  focusedCategoryId = null,
  focusedGroupKey = null,
  onFocusChange,
  layout,
  isError = false,
  onRetry,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const controlled = typeof onFocusChange === 'function';

  const [localFocus, setLocalFocus] = useState(null);
  const focus = controlled
    ? focusedCategoryId
      ? { groupKey: focusedGroupKey, categoryId: focusedCategoryId }
      : null
    : localFocus;

  const setFocus = useCallback(
    (next) => {
      if (controlled) onFocusChange(next);
      else setLocalFocus(next);
    },
    [controlled, onFocusChange],
  );

  useEffect(() => {
    if (!focus?.groupKey) return;
    setExpandedIds((prev) => {
      if (prev.has(focus.groupKey)) return prev;
      const next = new Set(prev);
      next.add(focus.groupKey);
      return next;
    });
  }, [focus?.groupKey]);

  const toggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // `.pen` `ds/card/budget-group` › `error`
  if (isError) {
    return (
      <div className="w-full min-w-0 overflow-hidden rounded-ds-lg border border-border bg-card">
        <CardErrorState title={pt.budget.groupsLoadError} onRetry={onRetry} />
      </div>
    );
  }

  if (!groups.length) return null;

  if (layout === 'desktop') {
    return (
      <div className="grid grid-cols-3 gap-space-5">
        {groups.map((group) => (
          <BudgetGroupSectionDesktop
            key={groupKey(group)}
            group={group}
            hasIncome={hasIncome}
            canEditBudgets={canEditBudgets}
            canAddCategory={canAddCategory}
            onAdjustDialog={onAdjustDialog}
            onCreateCategory={onCreateCategory}
            onFocusChange={setFocus}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-space-3 overflow-x-hidden">
      <ul className="flex w-full min-w-0 flex-col gap-space-3 overflow-x-hidden">
        {groups.map((group) => {
          const id = groupKey(group);
          const isThisFocus =
            focus?.groupKey === id ? focus.categoryId : null;
          return (
            <li key={id} className="w-full min-w-0">
              <BudgetGroupSection
                group={group}
                hasIncome={hasIncome}
                canEditBudgets={canEditBudgets}
                canAddCategory={canAddCategory}
                expanded={expandedIds.has(id)}
                onToggle={() => toggle(id)}
                onAdjustDialog={onAdjustDialog}
                onCreateCategory={onCreateCategory}
                firstCycle={firstCycle}
                focusPlacement={focusPlacement}
                focusedCategoryId={isThisFocus}
                onFocusChange={setFocus}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { resolveCategoryId };
